/**
 * Centered modal for a decision, drawn as finixt-ui's Dialog on Radix
 * Dialog. Answers are never gated behind one; actions are.
 *
 * Radix supplies the trap, Escape, the scrim click, scroll lock, and the
 * labelling; the restore to the opener is the layer's own, since the opener
 * is a button on the page and not a Radix Trigger. The icon tile beside the
 * title is what the app's modal had and finixt-ui's does not; its tint
 * follows the tone of what the dialog is about.
 */
import * as RadixDialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';

import { IconButton, IconTile } from './finixt.js';
import { useReturnFocus } from './useReturnFocus.js';

const SCRIM_Z_INDEX = 80;

export type DialogTone = 'neutral' | 'info' | 'ok' | 'warn' | 'danger' | 'accent';

const TILE: Record<DialogTone, { tint: string; color: string }> = {
  neutral: { tint: 'var(--surface-fill)', color: 'var(--text-muted)' },
  info: { tint: 'var(--info-tint)', color: 'var(--info)' },
  ok: { tint: 'var(--ok-tint)', color: 'var(--ok)' },
  warn: { tint: 'var(--warn-tint)', color: 'var(--warn)' },
  danger: { tint: 'var(--danger-tint)', color: 'var(--danger)' },
  accent: { tint: 'var(--accent-tint)', color: 'var(--text-accent)' },
};

export interface DialogProps {
  open: boolean;
  /** Fired by the scrim, the close button, and Escape. */
  onClose: () => void;
  title: ReactNode;
  /** One or two sentences naming the consequence. Also describes the dialog. */
  subtitle?: ReactNode;
  /** Material Symbols name for the tile beside the title. */
  icon?: string | undefined;
  /** The tile's tint. Default neutral. */
  tone?: DialogTone | undefined;
  /** Panel width in px. Default 460. */
  width?: number | undefined;
  /** Right-aligned action row: cancel first, primary last. */
  footer?: ReactNode;
  children?: ReactNode;
}

export function Dialog({
  open,
  onClose,
  title,
  subtitle,
  icon,
  tone = 'neutral',
  width = 460,
  footer,
  children,
}: DialogProps) {
  const onCloseAutoFocus = useReturnFocus(open);
  return (
    <RadixDialog.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: SCRIM_Z_INDEX,
            background: 'var(--scrim)',
            animation: 'fadein var(--dur-fast) var(--ease-out)',
          }}
        />
        <RadixDialog.Content
          onCloseAutoFocus={onCloseAutoFocus}
          {...(subtitle ? {} : { 'aria-describedby': undefined })}
          style={{
            position: 'fixed',
            zIndex: SCRIM_Z_INDEX + 1,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width,
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 'calc(100vh - 48px)',
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--surface-card)',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius-sheet)',
            boxShadow: 'var(--shadow-sheet)',
            outline: 'none',
            animation: 'popin var(--dur-enter) var(--ease-out)',
          }}
        >
          <div
            style={{ display: 'flex', alignItems: 'flex-start', gap: 11, padding: '16px 18px 0' }}
          >
            {icon ? <IconTile icon={icon} size={40} {...TILE[tone]} /> : null}
            <div style={{ minWidth: 0, flex: 1 }}>
              <RadixDialog.Title
                style={{
                  margin: 0,
                  fontSize: 'var(--text-title)',
                  fontWeight: 'var(--weight-bold)',
                  letterSpacing: 'var(--tracking-title)',
                  color: 'var(--text-strong)',
                }}
              >
                {title}
              </RadixDialog.Title>
              {subtitle ? (
                <RadixDialog.Description
                  style={{
                    margin: 0,
                    marginTop: 5,
                    fontSize: 'var(--text-body-sm)',
                    color: 'var(--text-muted)',
                    lineHeight: 'var(--leading-body)',
                  }}
                >
                  {subtitle}
                </RadixDialog.Description>
              ) : null}
            </div>
            <RadixDialog.Close asChild>
              <IconButton icon="close" size={28} tone="bare" aria-label="Close" />
            </RadixDialog.Close>
          </div>
          <div style={{ padding: '14px 18px', overflowY: 'auto', minHeight: 0 }}>{children}</div>
          {footer ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 7,
                padding: '11px 18px',
                borderTop: '1px solid var(--border-divider)',
              }}
            >
              {footer}
            </div>
          ) : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/**
 * The action row drawn inside the body, for a footer that must stay inside
 * the form it submits. It bleeds to the panel's edges and draws the same
 * hairline as the `footer` slot, so the two are indistinguishable.
 */
export function DialogActions({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 7,
        margin: '14px -18px -14px',
        padding: '11px 18px',
        borderTop: '1px solid var(--border-divider)',
      }}
    >
      {children}
    </div>
  );
}
